import React from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, Lock, Plus } from 'lucide-react';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';
import { ModuleLink } from '../../../../components/ModuleLink';
import { InvoicePreviewModalHost } from '../../../invoices/components/InvoicePreviewModalHost';
import type { InvoiceStatus } from '../../../invoices/types';
import { statusDisplayLabel } from '../../../invoices/utils/invoicePreviewUi';
import { isIncomingCreditDocumentType } from '../../../invoices/utils/incomingInvoice';
import { invoicesOverviewPath, type InvoiceTab } from '../../../../utils/invoiceRoutes';
import { formatDisplayHours } from '../../../../utils/employeeTimeEntryHours';
import { TimeEntryIntervalModal } from '../../../../components/timeEntries/TimeEntryIntervalModal';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import {
  notifyLeadTimeEntriesChanged,
  useLeadProjectTimeEntries,
  useLeadTimeEntriesChanged,
} from './LeadProjectTimeEntries';
import { LeadIstHoursOverviewModal } from './LeadIstWeeks';
import type { LeadProjectTimeEntry } from './projectActivityIstHours';

type InvoiceRow = {
  id: string;
  invoice_number: string | null;
  party_name: string | null;
  issue_date: string | null;
  net_amount: number | string | null;
  gross_amount: number | string | null;
  retention_gross?: number | string | null;
  retention_status?: string | null;
  status: string | null;
  filename: string | null;
  document_type?: string | null;
};

type LaborRow = {
  employee_id: string;
  name: string;
  hours: number;
  hourly_wage: number;
  /** Zulage pro Stunde, included in `amount`. */
  allowance_per_hour?: number;
  amount: number;
};

type LeadInvoicesResponse = {
  incoming?: InvoiceRow[];
  outgoing?: InvoiceRow[];
  items?: InvoiceRow[];
  labor?: LaborRow[];
  totals: {
    outgoing_net: number;
    incoming_net: number;
    gross_margin: number;
    outgoing_missing_net?: number;
    incoming_missing_net?: number;
    labor_cost?: number;
    labor_hours?: number;
    planned_hours?: number;
    contribution_margin?: number;
  };
};

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

function formatMoney(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  return eur.format(Number.isFinite(n) ? n : 0);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function hoursLabel(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${formatDisplayHours(n)} h`;
}

function rowStatusLabel(
  status: string | null | undefined,
  tab: InvoiceTab,
  documentType?: string | null,
): string {
  const raw = String(status ?? '').trim();
  if (!raw) return '—';
  return statusDisplayLabel(raw as InvoiceStatus, tab, { document_type: documentType as never }) || raw;
}

/** Quiet status text: only what needs attention gets color, done rows stay grey. */
function statusToneClass(status: string | null | undefined): { text: string; dot: string } {
  const raw = String(status ?? '').trim().toLowerCase();
  if (raw === 'overdue' || raw === 'extraction_failed' || raw === 'rejected') {
    return { text: 'text-rose-700', dot: 'bg-rose-500' };
  }
  if (raw === 'draft' || raw === 'extraction_pending') return { text: 'text-amber-800', dot: 'bg-amber-500' };
  if (raw === 'paid' || raw === 'cancelled') return { text: 'text-app-text-subtle', dot: 'bg-app-text-subtle/50' };
  return { text: 'text-app-text-muted', dot: 'bg-pipedrive-blue' };
}

const VISIBLE_ROWS = 5;

/** First rows of a sidebar list; the rest behind „N weitere“ so the sidebar stays short. */
function useCollapsedRows<T>(items: T[]) {
  const [expanded, setExpanded] = React.useState(false);
  const hidden = Math.max(0, items.length - VISIBLE_ROWS);
  const visible = expanded || hidden === 0 ? items : items.slice(0, VISIBLE_ROWS);
  const toggle =
    hidden > 0 ? (
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="pt-1 text-[12px] font-medium text-app-text-subtle hover:text-pipedrive-blue"
      >
        {expanded ? 'Weniger anzeigen' : `${hidden} weitere anzeigen`}
      </button>
    ) : null;
  return { visible, toggle };
}

function marginToneClass(value: number): string {
  if (value < 0) return 'text-rose-700';
  if (value > 0) return 'text-emerald-700';
  return 'text-app-text';
}

function useLeadInvoices(leadId: string) {
  const [data, setData] = React.useState<LeadInvoicesResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);

  const reload = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const silent = reloadToken > 0;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    fetch(`/api/leads/${encodeURIComponent(leadId)}/incoming-invoices`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || 'Rechnungen konnten nicht geladen werden.');
        return r.json() as Promise<LeadInvoicesResponse>;
      })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Rechnungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, reloadToken]);

  return { data, loading, error, reload };
}

function InvoiceGroup({
  title,
  href,
  emptyLabel,
  fallbackName,
  tab,
  net,
  missingNet = 0,
  items,
  onOpen,
}: {
  title: string;
  href: string;
  emptyLabel: string;
  fallbackName: string;
  tab: InvoiceTab;
  net: number;
  missingNet?: number;
  items: InvoiceRow[];
  onOpen: (row: InvoiceRow) => void;
}) {
  const { visible, toggle } = useCollapsedRows(items);
  return (
    <section className="border-t border-app-border py-3 first:border-t-0 first:pt-3">
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate">
          <ModuleLink
            to={href}
            className="text-[13px] font-semibold text-app-text hover:text-pipedrive-blue hover:underline"
          >
            {title}
          </ModuleLink>
        </h2>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums text-app-text">{formatMoney(net)}</span>
      </header>
      {missingNet > 0 ? (
        <p className="mb-1 text-[12px] text-amber-800">
          {missingNet === 1
            ? '1 Rechnung ohne Nettobetrag ist nicht enthalten.'
            : `${missingNet} Rechnungen ohne Nettobetrag sind nicht enthalten.`}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="py-1.5 text-[13px] text-app-text-subtle">{emptyLabel}</p>
      ) : (
        <>
          <ul>
            {visible.map((row) => {
              const tone = statusToneClass(row.status);
              const retentionHeld = row.retention_status === 'held';
              return (
                <li key={row.id} className="flex items-start justify-between gap-3 py-1.5">
                  <button
                    type="button"
                    onClick={() => onOpen(row)}
                    className="group min-w-0 flex-1 text-left"
                    title={row.filename ?? 'Beleg öffnen'}
                  >
                    <span className="block truncate text-[13px] text-app-text group-hover:underline">
                      {row.party_name || fallbackName}
                      {row.invoice_number ? ` · ${row.invoice_number}` : ''}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[12px] text-app-text-subtle">
                      <span>{formatDate(row.issue_date)}</span>
                      <span aria-hidden>·</span>
                      <span className={`inline-flex items-center gap-1 ${tone.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden />
                        {rowStatusLabel(row.status, tab, row.document_type)}
                      </span>
                      {tab === 'outgoing' && Number(row.retention_gross) > 0 ? (
                        <>
                          <span aria-hidden>·</span>
                          <span
                            className={`inline-flex items-center gap-0.5 ${retentionHeld ? 'text-amber-800' : ''}`}
                            title={
                              retentionHeld
                                ? 'Sicherheitseinbehalt offen'
                                : row.retention_status === 'released_on_final'
                                  ? 'Auf Schlussrechnung freigegeben'
                                  : 'Sicherheitseinbehalt'
                            }
                          >
                            {retentionHeld ? <Lock className="h-2.5 w-2.5" /> : null}
                            Einbehalt {formatMoney(row.retention_gross)}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </button>
                  {row.net_amount == null ? (
                    <span
                      className="shrink-0 pt-0.5 text-right text-[12px] text-amber-800"
                      title={row.gross_amount != null ? `Brutto ${formatMoney(row.gross_amount)}` : undefined}
                    >
                      Netto fehlt
                    </span>
                  ) : (
                    <span className={`shrink-0 pt-0.5 text-[13px] tabular-nums ${isIncomingCreditDocumentType(row.document_type) ? 'text-emerald-700' : 'text-app-text'}`}>
                      {formatMoney(row.net_amount)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {toggle}
        </>
      )}
    </section>
  );
}

function ResultRow({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: number;
  emphasize?: boolean;
}) {
  if (emphasize) {
    return (
      <section className="border-t border-app-border py-3">
        <div className="rounded-lg bg-app-surface-muted px-3 py-3">
          <div className="text-[13px] font-medium text-app-text-subtle">{label}</div>
          <div className={`mt-0.5 text-xl font-semibold tabular-nums leading-tight ${marginToneClass(value)}`}>
            {formatMoney(value)}
          </div>
        </div>
      </section>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-app-border py-3">
      <span className="text-[13px] font-semibold text-app-text">{label}</span>
      <span className={`shrink-0 text-[13px] font-semibold tabular-nums ${marginToneClass(value)}`}>
        {formatMoney(value)}
      </span>
    </div>
  );
}

function LaborSection({
  cost,
  hours,
  plannedHours,
  items,
  onAdd,
  onOpenOverview,
}: {
  cost: number;
  hours: number;
  plannedHours: number;
  items: LaborRow[];
  /** Set when the user may record Ist times (appointment or project without appointment). */
  onAdd?: () => void;
  /** Opens the Ist times by week; set when there are any. */
  onOpenOverview?: () => void;
}) {
  const { visible, toggle } = useCollapsedRows(items);
  const istLabel = hoursLabel(hours);
  const plannedLabel = hoursLabel(plannedHours);
  const metaParts = [
    istLabel ? `${istLabel} gestempelt` : null,
    plannedLabel && plannedHours !== hours ? `${plannedLabel} geplant` : null,
  ].filter(Boolean);

  return (
    <section className="border-t border-app-border py-3">
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {onOpenOverview ? (
            <button
              type="button"
              onClick={onOpenOverview}
              className="group inline-flex min-w-0 items-center gap-0.5 text-left"
              title="Ist-Zeiten nach Woche anzeigen"
            >
              <h2 className="min-w-0 truncate text-[13px] font-semibold text-app-text group-hover:text-pipedrive-blue">
                Personalaufwand
              </h2>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-app-text-subtle group-hover:text-pipedrive-blue" />
            </button>
          ) : (
            <h2 className="min-w-0 truncate text-[13px] font-semibold text-app-text">Personalaufwand</h2>
          )}
          {onAdd ? (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-medium text-app-text-subtle hover:text-pipedrive-blue"
            >
              <Plus className="h-3 w-3" />
              Ist-Zeit
            </button>
          ) : null}
        </div>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums text-app-text">{formatMoney(cost)}</span>
      </header>
      {items.length === 0 ? (
        <p className="py-1.5 text-[13px] text-app-text-subtle">Noch keine gestempelten Stunden</p>
      ) : (
        <>
          <ul>
            {visible.map((row) => {
              // Without a wage the cost is unknown, not zero — showing 0,00 € here
              // reads as "this Termin was free".
              const wageMissing = row.hourly_wage <= 0;
              return (
                <li key={row.employee_id} className="flex items-start justify-between gap-3 py-1.5">
                  <div className="min-w-0">
                    <span className="block truncate text-[13px] text-app-text">{row.name}</span>
                    <span className="mt-0.5 block text-[12px] text-app-text-subtle">
                      {hoursLabel(row.hours) || '—'}
                      {wageMissing
                        ? ' · Kein Stundenlohn hinterlegt'
                        : ` · ${formatMoney(row.hourly_wage)}/h${
                            (row.allowance_per_hour ?? 0) > 0 ? ` + ${formatMoney(row.allowance_per_hour)} Zulage` : ''
                          }`}
                    </span>
                  </div>
                  <span className="shrink-0 pt-0.5 text-[13px] tabular-nums text-app-text">
                    {wageMissing ? '—' : formatMoney(row.amount)}
                  </span>
                </li>
              );
            })}
          </ul>
          {toggle}
        </>
      )}
      {metaParts.length > 0 ? (
        <p className="pt-1 text-[12px] text-app-text-subtle">{metaParts.join(' · ')}</p>
      ) : plannedLabel ? (
        <p className="pt-1 text-[12px] text-app-text-subtle">{plannedLabel} geplant</p>
      ) : null}
    </section>
  );
}

export function LeadIncomingInvoicesCard({
  leadId,
  variant = 'card',
}: {
  leadId: string;
  variant?: 'card' | 'sidebar';
}) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data, loading, error, reload } = useLeadInvoices(leadId);
  const [previewInvoiceId, setPreviewInvoiceId] = React.useState<string | null>(null);
  const { can } = useCapabilities();
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Personalaufwand und Ist-Zeiten nur mit Zeiterfassung (Modul Mitarbeiter).
  const hrEnabled = modulesLoaded && isEnabled('hr');
  const canEditIst = can('web.projekte.edit') && hrEnabled;
  const { items: timeEntries, activities: timeActivities } = useLeadProjectTimeEntries(leadId);
  // `entry` null = new Ist time; set when a day is edited from the week overview.
  const [istDialog, setIstDialog] = React.useState<{ entry: LeadProjectTimeEntry | null } | null>(null);
  const [overviewOpen, setOverviewOpen] = React.useState(false);
  // Labor totals change whenever an Ist time is saved anywhere on the project page.
  useLeadTimeEntriesChanged(leadId, reload);
  const incoming = data?.incoming ?? data?.items ?? [];
  const outgoing = data?.outgoing ?? [];
  const labor = data?.labor ?? [];
  const totals = data?.totals;
  const heldRetentions = outgoing.filter(
    (row) => row.retention_status === 'held' && Number(row.retention_gross) > 0,
  );
  const heldRetentionTotal = heldRetentions.reduce(
    (sum, row) => sum + (Number(row.retention_gross) || 0),
    0,
  );
  const incomingPath = instanceId ? invoicesOverviewPath(instanceId, 'incoming') : '/invoices/incoming';
  const outgoingPath = instanceId ? invoicesOverviewPath(instanceId, 'outgoing') : '/invoices/outgoing';
  const sidebar = variant === 'sidebar';
  const rohertrag = totals?.gross_margin ?? 0;
  const laborCost = totals?.labor_cost ?? 0;
  const contribution = totals?.contribution_margin ?? rohertrag - laborCost;

  return (
    <div className={sidebar ? 'flex flex-1 flex-col' : 'rounded-xl border border-app-border bg-app-surface px-4'}>
      {loading ? (
        <div className="flex min-h-full flex-1 items-center justify-center px-3 py-6">
          <InlineLoadingState
            label="Controlling wird geladen …"
            className="max-w-[16rem] flex-col text-center"
          />
        </div>
      ) : error ? (
        <p className="py-4 text-[13px] text-rose-700">{error}</p>
      ) : (
        <>
          <InvoiceGroup
            title="Betriebliche Erträge"
            href={outgoingPath}
            emptyLabel="Noch keine Ausgangsrechnungen"
            fallbackName="Kunde"
            tab="outgoing"
            net={totals?.outgoing_net ?? 0}
            missingNet={totals?.outgoing_missing_net ?? 0}
            items={outgoing}
            onOpen={(row) => setPreviewInvoiceId(row.id)}
          />
          <InvoiceGroup
            title="Material / Fremdleistung"
            href={incomingPath}
            emptyLabel="Noch keine Eingangsrechnungen"
            fallbackName="Lieferant"
            tab="incoming"
            net={totals?.incoming_net ?? 0}
            missingNet={totals?.incoming_missing_net ?? 0}
            items={incoming}
            onOpen={(row) => setPreviewInvoiceId(row.id)}
          />
          <ResultRow label="Rohertrag" value={rohertrag} />
          {hrEnabled ? (
            <>
              <LaborSection
                cost={laborCost}
                hours={totals?.labor_hours ?? 0}
                plannedHours={totals?.planned_hours ?? 0}
                items={labor}
                onAdd={canEditIst ? () => setIstDialog({ entry: null }) : undefined}
                onOpenOverview={timeEntries.length > 0 ? () => setOverviewOpen(true) : undefined}
              />
              <ResultRow label="Deckungsbeitrag" value={contribution} emphasize />
            </>
          ) : null}
          {heldRetentions.length > 0 ? (
            <section className="border-t border-app-border py-3">
              <header className="mb-1.5 flex items-center justify-between gap-2">
                <h2 className="min-w-0 truncate text-[13px] font-semibold text-amber-900">
                  Offene Einbehalte
                </h2>
                <span className="shrink-0 text-[13px] font-semibold tabular-nums text-amber-900">
                  {formatMoney(heldRetentionTotal)}
                </span>
              </header>
              <ul>
                {heldRetentions.map((row) => (
                  <li key={row.id} className="flex items-start justify-between gap-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => setPreviewInvoiceId(row.id)}
                      className="group min-w-0 flex-1 text-left"
                      title={row.filename ?? 'Beleg öffnen'}
                    >
                      <span className="block truncate text-[13px] text-app-text group-hover:underline">
                        {row.invoice_number ? `Rechnung ${row.invoice_number}` : row.party_name || 'Rechnung'}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[12px] text-app-text-subtle">{formatDate(row.issue_date)}</span>
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[11px] font-medium text-amber-900">
                          <Lock className="h-2.5 w-2.5" />
                          Offen bis Schlussrechnung
                        </span>
                      </span>
                    </button>
                    <span className="shrink-0 pt-0.5 text-[13px] tabular-nums text-amber-900">
                      {formatMoney(row.retention_gross)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
      <LeadIstHoursOverviewModal
        open={overviewOpen}
        onClose={() => setOverviewOpen(false)}
        items={timeEntries}
        onEdit={canEditIst ? (row) => setIstDialog({ entry: row }) : undefined}
      />
      {canEditIst ? (
        <TimeEntryIntervalModal
          open={istDialog !== null}
          onClose={() => setIstDialog(null)}
          onSaved={() => {
            setIstDialog(null);
            notifyLeadTimeEntriesChanged(leadId);
          }}
          scope={{ kind: 'lead', leadId, activities: timeActivities }}
          entry={istDialog?.entry ?? null}
          lockedActivityId={istDialog?.entry?.activity_id ?? null}
        />
      ) : null}
      {previewInvoiceId ? (
        <InvoicePreviewModalHost
          invoiceId={previewInvoiceId}
          onClose={() => setPreviewInvoiceId(null)}
          onPatched={reload}
        />
      ) : null}
    </div>
  );
}
